/**
 * PlatformLayout — the frame inside a platform. Since the 0.53.0 redesign the six sections
 * (Find, Operations, Monitoring, Configuration, Access and roles, Tools) and their pages are in the
 * shell's sidebar and the platform API's version is in the top bar, so this only wraps the page.
 * The props are kept so every platform area calls it as before.
 *
 * Every page is its own address (pages, not pop-ups): section landings live at
 * `${basePath}/${sectionId}`.
 */

import { Outlet } from 'react-router';
import type { ReactNode } from 'react';
import { EmptyState, PageHeader } from '@retail-ops/panel-shell';
import type { SectionId } from '@/platforms/types';

export interface PlatformLayoutProps {
  title: ReactNode;
  basePath: string;
  /** Sections this person has something in (the sidebar shows them). */
  enabled: ReadonlySet<string>;
  /** Which section a path belongs to (null = the platform's home). */
  sectionOf: (pathname: string) => SectionId | string | null;
  /** Formerly the header's right side (the API version); the top bar shows it now. */
  aside?: ReactNode;
}

export function PlatformLayout(_props: PlatformLayoutProps): JSX.Element {
  return (
    <div className="platform__body">
      <Outlet />
    </div>
  );
}

/** "Not yet available" for a platform whose module has not landed (Finance). */
export function PlatformPlaceholder({ name }: { name: string }): JSX.Element {
  return (
    <div>
      <PageHeader title={name} />
      <EmptyState title={`${name} administration is not available here yet.`}>
        It joins this portal when the {name} team&rsquo;s module lands. Until then, use the platform switcher in the menu to go to a
        platform you administer.
      </EmptyState>
    </div>
  );
}
